import { motion } from "motion/react";
import type { ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import lock01Icon from "@/assets/icons/lock-01.svg";
import type { DesignSystemElementProps, DesignSystemRef } from "@/design-system/contracts/dom";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";

const ACTIVITY_COMMENT_TONE_CLASSES = {
  default: "first:pt-0",
  internal: "bg-background-warning-secondary",
} as const;

export type ActivityCommentProps = {
  avatar: ReactNode;
  identity: ReactNode;
  timestamp: ReactNode;
  marker?: ReactNode;
  children: ReactNode;
  tone?: keyof typeof ACTIVITY_COMMENT_TONE_CLASSES;
} & Omit<
  DesignSystemElementProps<"article">,
  "onAnimationEnd" | "onAnimationIteration" | "onAnimationStart" | "onDrag" | "onDragEnd" | "onDragStart"
> &
  DesignSystemRef<HTMLElement>;

/** Animated ticket-comment shell. Content, permissions, and actions remain product-owned. */
export function ActivityComment({
  avatar,
  children,
  identity,
  marker,
  ref,
  timestamp,
  tone = "default",
  ...nativeProps
}: ActivityCommentProps): ReactNode {
  return (
    <motion.article
      {...nativeProps}
      ref={ref}
      className={twJoin("flex gap-space-sm rounded-sm p-space-sm", ACTIVITY_COMMENT_TONE_CLASSES[tone])}
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, height: 0, marginBottom: "0px" }}
    >
      <div className="flex h-control-xl items-center">{avatar}</div>
      <section className="grid flex-1 grid-cols-1">
        <Inline align="start" wrap={false}>
          <Inline gap="sm" align="center">
            {identity}
            <span className="text-caption text-text-secondary">{timestamp}</span>
          </Inline>
          {marker}
        </Inline>
        <Stack gap="sm" align="start">
          {children}
        </Stack>
      </section>
    </motion.article>
  );
}

export function ActivityCommentMarker({ children }: { children: ReactNode }): ReactNode {
  return (
    <span className="ml-auto flex h-icon-prominent items-center gap-space-xs text-caption text-text-warning">
      <span aria-hidden="true" className="block shrink-0 fill-transparent">
        <svg width={16} height={16}>
          <use xlinkHref={`#${lock01Icon}`} />
        </svg>
      </span>
      <span className="hidden text-caption @sm:block">{children}</span>
    </span>
  );
}

export type ActivityCommentContentProps = {
  children: ReactNode;
} & DesignSystemElementProps<"p"> &
  DesignSystemRef<HTMLParagraphElement>;

export function ActivityCommentContent({ children, ref, ...nativeProps }: ActivityCommentContentProps): ReactNode {
  return (
    <p {...nativeProps} ref={ref} className="max-w-measure-wide whitespace-pre-line">
      {children}
    </p>
  );
}

export type ActivityCommentItemProps = {
  children: ReactNode;
} & DesignSystemElementProps<"div"> &
  DesignSystemRef<HTMLDivElement>;

export function ActivityCommentItem({ children, ref, ...nativeProps }: ActivityCommentItemProps): ReactNode {
  return (
    <div {...nativeProps} ref={ref} className="max-w-full">
      {children}
    </div>
  );
}
